import React from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import { Link, withRouter } from 'react-router';
import { FormattedMessage } from 'react-intl';
import Icon from '../Icon';
import Button from '../Button';
import Input from '../Input';
import Loader from '../Loader';
import Dimmer from '../Dimmer';
import Header from '../Header';
import Form from '../Form';
import Grid from '../Grid';
import Message from '../Message';
import PasswordRules from '../PasswordRules';
import { missingValueLabel, missingPasswordLabel, missingNewPasswordLabel, missingConfirmPasswordLabel, passwordMismatchLabel, passwordNotSecureLabel } from './labelComponents';
import { renderUserProfileModal, renderHEInstitutionProfileModal } from '../../utils/counselorCommunityModals';
import messages from './messages';
import './styles.css';

function validateForm(formData) {
  let secure = true;
  if (formData.newPassword) {
    const regEx = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\]\-^$*.[()?"!@#%&/,><':;|_~`])[A-Za-z\d\]\-^$*.[()?"!@#%&/,><':;|_~`]{8,}/;
    secure = regEx.test(formData.newPassword);
  }
  return {
    missingFirstName: !formData.firstName,
    missingLastName: !formData.lastName,
    missingEmail: !formData.email,
    missingPassword: !formData.password && (formData.newPassword || formData.confirmPassword),
    missingNewPassword: formData.password && !formData.newPassword,
    missingConfirmPassword: formData.password && !formData.confirmPassword,
    passwordMismatch: formData.newPassword !== formData.confirmPassword,
    passwordNotSecure: !secure,
  };
}

export const elementIds = {
  firstNameId: 'user-form-first-name',
  lastNameId: 'user-form-last-name',
  emailId: 'user-form-email',
  currentPasswordId: 'current-password-input',
  newPasswordId: 'new-password-input',
  confirmPasswordId: 'confirm-password-input',
};

export class ChangeProfileForm extends React.PureComponent {
  static propTypes = {
    user: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }).isRequired,
    requesting: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    updateError: PropTypes.shape({
      generalError: PropTypes.bool,
      userExists: PropTypes.bool,
    }).isRequired,
    isCommunityServerlessOn: PropTypes.bool,
    uuid: PropTypes.string,
    viewer: PropTypes.object,
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      email: props.user.email,
      errors: {},
      showUserModal: false,
      showCollegeModal: false,
      showHighschoolModal: false,
      userId: null,
      collegeId: null,
      highschoolId: null,
      idType: null,
    };
  }

  getModifiedFormFields = (formData) => {
    const modifiedFormFields = {};
    if (formData.firstName) {
      if (!this.props.user.firstName || formData.firstName.trim() !== this.props.user.firstName.trim()) {
        modifiedFormFields.firstName = formData.firstName.trim();
      }
    }
    if (formData.lastName) {
      if (!this.props.user.lastName || formData.lastName.trim() !== this.props.user.lastName.trim()) {
        modifiedFormFields.lastName = formData.lastName.trim();
      }
    }
    if (formData.email) {
      if (!this.props.user.email || formData.email.trim() !== this.props.user.email.trim()) {
        modifiedFormFields.email = formData.email.trim();
      }
    }
    if (formData.password) {
      modifiedFormFields.password = formData.password;
    }
    if (formData.newPassword) {
      modifiedFormFields.newPassword = formData.newPassword;
    }
    return modifiedFormFields;
  };

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    const formData = omit(this.state, 'errors');
    e.preventDefault();
    const errors = validateForm(formData);
    this.setState({ errors });
    if (!Object.keys(errors).some((key) => errors[key])) {
      const modifiedFormFields = this.getModifiedFormFields(formData);
      this.props.onSave(modifiedFormFields);
    }
  };

  closeModals = (modalOptions) => this.setState({ ...modalOptions });

  handleViewStaff = (event, institutionName, institutionType, institutionSubType) => {
    event?.preventDefault();
    this.props.router.push('/counselorcommunity/search/people?' +
      `viewStaffInstitutionName=${institutionName}&` +
      `viewStaffInstitutionType=${institutionType}&` +
      `viewStaffInstitutionSubType=${institutionSubType}`);
    window.dispatchEvent(new Event('counselor-community-serverless-update-route'));
  };

  openInstitutionProfileModal = ({ collegeId, highschoolId, idType }) => {
    this.closeModals();
    this.setState({
      showCollegeModal: !!collegeId,
      showHighschoolModal: !!highschoolId,
      collegeId,
      highschoolId,
      idType,
    });
  };

  render() {
    const { updateError={}, requesting, isCommunityServerlessOn, uuid } = this.props;
    const { errors, firstName, lastName, email, showUserModal, showCollegeModal, idType, collegeId } = this.state;
    const { firstNameId, lastNameId, emailId, currentPasswordId, newPasswordId, confirmPasswordId } = elementIds;
    const firstNameErrorLabel = errors.missingFirstName ? missingValueLabel : '';
    const lastNameErrorLabel = errors.missingLastName ? missingValueLabel : '';
    const emailErrorLabel = errors.missingEmail ? missingValueLabel : '';
    const passwordErrorLabel = errors.missingPassword ? missingPasswordLabel : '';
    const newPasswordErrorLabel = errors.missingNewPassword ? missingNewPasswordLabel : '';
    const confirmPasswordErrorLabel = errors.missingConfirmPassword ? missingConfirmPasswordLabel : '';
    const passwordMismatchErrorLabel = errors.passwordMismatch ? passwordMismatchLabel : '';
    const passwordNotSecureErrorLabel = errors.passwordNotSecure ? passwordNotSecureLabel : '';

    const errorMessage = Object.keys(updateError).map((errorKey) => (
      <Message role="alert" negative key={errorKey}>
        <Message.Header>
          <FormattedMessage {...messages[errorKey]} />
        </Message.Header>
      </Message>
    ));

    return (
      <React.Fragment>
        {renderUserProfileModal(showUserModal, uuid, this.props, this.closeModals, this.openInstitutionProfileModal)}
        {renderHEInstitutionProfileModal(showCollegeModal, idType, collegeId, this.props, this.closeModals, this.handleViewStaff)}
        <Dimmer.Dimmable as={Form} id="form-update-user" onSubmit={this._onSubmit} size="large">
          <Dimmer
            role="progressbar"
            aria-describedby="Updating User..."
            aria-busy={requesting}
            inverted
            active={requesting}
          >
            <Loader active={requesting} />
          </Dimmer>
          <Header as="h3" styleName="form-section-heading" id="your-name-group">
            <FormattedMessage {...messages.yourName} />
          </Header>
          <Grid role="group" aria-labelledby="your-name-group">
            <Grid.Column width={5}>
              <Grid.Row>
                <Form.Field styleName="change-profile-form-field">
                  <label htmlFor={firstNameId}>
                    <FormattedMessage {...messages.firstName} />
                  </label>
                  <Input
                    id={firstNameId}
                    required
                    aria-invalid={!!firstNameErrorLabel}
                    type="text"
                    name="firstName"
                    value={firstName}
                    onChange={this._onChange}
                  />
                  {firstNameErrorLabel}
                </Form.Field>
              </Grid.Row>
            </Grid.Column>
            <Grid.Column width={5}>
              <Grid.Row>
                <Form.Field styleName="change-profile-form-field">
                  <label htmlFor={lastNameId}>
                    <FormattedMessage {...messages.lastName} />
                  </label>
                  <Input
                    id={lastNameId}
                    required
                    aria-invalid={!!lastNameErrorLabel}
                    type="text"
                    name="lastName"
                    value={lastName}
                    onChange={this._onChange}
                  />
                  {lastNameErrorLabel}
                </Form.Field>
              </Grid.Row>
            </Grid.Column>
          </Grid>
          <p>
            <FormattedMessage
              {...messages.gotoYourProfile}
              values={{
                yourProfile: isCommunityServerlessOn ? (
                  <Link to="/settings/change-profile" onClick={() => this.setState({ showUserModal: true })} className="user-profile-link">
                    <FormattedMessage {...messages.yourProfile} />
                  </Link>
                ) :
                  <Link to="/counselor-community/profile" onlyActiveOnIndex><FormattedMessage {...messages.yourProfile} /></Link>,
              }}
            />
          </p>

          <Header as="h3" styleName="form-section-heading" id="contact-info-group">
            <FormattedMessage {...messages.contactInformation} />
          </Header>
          <Form.Field styleName="change-profile-form-field" role="group" aria-labelledby="contact-info-group">
            <label htmlFor={emailId}>
              <FormattedMessage {...messages.email} />
            </label>
            <Input
              id={emailId}
              aria-required
              pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$"
              required
              aria-invalid={!!emailErrorLabel}
              type="email"
              name="email"
              value={email}
              autoComplete="off"
              onChange={this._onChange}
            />
            {emailErrorLabel}
          </Form.Field>

          <Header as="h3" styleName="form-section-heading" id="change-password-group">
            <FormattedMessage {...messages.changePasswordSection} />
          </Header>
          <Grid role="group" aria-labelledby="change-password-group">
            <Grid.Row>
              <Grid.Column width={16}>
                <Form.Field styleName="change-profile-form-field">
                  <label htmlFor={currentPasswordId}>
                    <FormattedMessage {...messages.password} />
                  </label>
                  <Input
                    type="password"
                    id={currentPasswordId}
                    autoComplete="off"
                    aria-invalid={!!passwordErrorLabel}
                    name="password"
                    icon="lock"
                    iconPosition="left"
                    onChange={this._onChange}
                  />
                  {passwordErrorLabel}
                </Form.Field>
                <Form.Field styleName="change-profile-form-field">
                  <label htmlFor={newPasswordId}>
                    <FormattedMessage {...messages.newPassword} />
                  </label>
                  <Input
                    type="password"
                    id={newPasswordId}
                    autoComplete="off"
                    aria-invalid={!!newPasswordErrorLabel || !!passwordNotSecureErrorLabel}
                    name="newPassword"
                    icon="lock"
                    iconPosition="left"
                    onChange={this._onChange}
                  />
                  {newPasswordErrorLabel}
                  {passwordNotSecureErrorLabel}
                  <PasswordRules />
                </Form.Field>
                <Form.Field styleName="change-profile-form-field">
                  <label htmlFor={confirmPasswordId}>
                    <FormattedMessage {...messages.confirmPassword} />
                  </label>
                  <Input
                    type="password"
                    id={confirmPasswordId}
                    aria-invalid={!!confirmPasswordErrorLabel || !!passwordMismatchErrorLabel}
                    name="confirmPassword"
                    icon="lock"
                    iconPosition="left"
                    autoComplete="off"
                    onChange={this._onChange}
                  />
                  {confirmPasswordErrorLabel}
                  {passwordMismatchErrorLabel}
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {errorMessage}
          <div styleName="button-section">
            <Button icon labelPosition="left" primary type="submit" data-cy="account-information-save-button">
              <Icon name="checkmark" />
              <FormattedMessage {...messages.buttonSave} />
            </Button>
          </div>

        </Dimmer.Dimmable>
      </React.Fragment>
    );
  }
}

export default withRouter(ChangeProfileForm);
